<template>
  <div id="app">
    <transition name="layout">
      <component :is="layoutName" class="layout" />
    </transition>
    <dialogs-wrapper tag="div" :id="'modal_popup'"></dialogs-wrapper>
    <dialogs-wrapper tag="div" name="error" :id="'modal_error_popup'"></dialogs-wrapper>
  </div>
</template>

<script>

import '@/styles/app.scss'

import { mapState, mapMutations } from 'vuex'
import { storageManager } from '@/libs/storage-manager'
import { create } from 'vue-modal-dialogs'
import { getAuthToken, getAuthCookie, removeAuthData } from '@/libs/auth-helper'
import { clearAllApiCache } from '@/libs/api-request'

import DefaultLayout from '@/layouts/DefaultLayout'
import DashboardLayout from '@/layouts/DashboardLayout'
import NoHeaderLayout from '@/layouts/NoHeaderLayout'
import BridgeLayout from '@/layouts/BridgeLayout'
import MyInfoLayout from '@/layouts/MyInfoLayout'
import Error500 from '@/components/layerpop/error500'
import { browserInfo } from '@/libs/utils'

let errorDialog = create({
  component: Error500,
  wrapper: 'error'
})

export default {
  name: 'App',
  components: {
    'default-layout': DefaultLayout,
    'dashboard-layout': DashboardLayout,
    'no-header-layout': NoHeaderLayout,
    'bridge-layout': BridgeLayout,
    'myinfo-layout': MyInfoLayout
  },
  data () {
    return {
      hasError: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'errorData'
    ]),
    layoutName () {
      if (!this.$route.matched.length) {
        return null
      }
      const route = Array.from(this.$route.matched).reverse()
        .find(item => item.meta.hasOwnProperty('layout'))

      const layout = route ? route.meta.layout : 'default'
      return `${layout}-layout`
    }
  },
  watch: {
    errorData (value) {
      if (value && !this.hasError) {
        this.$router.replace({ name: 'dashboard', params: { error: value } })
        this.hasError = true
        errorDialog().then(() => {
          this.setErrorData(null)
          this.hasError = false
        })
      }
    },
    $route: {
      async handler (current) {
        if (current.name !== null) {
          const popupDiv = document.getElementById('modal_popup')
          if (popupDiv) {
            popupDiv.innerHTML = ''
          }

          const authToken = getAuthToken()
          if (browserInfo.name && browserInfo.name === 'ie' && current.name !== 'bridge') {
            removeAuthData()
          } else {
            this.syncAuthTokenAndCookie()
            const guestOnly = current.meta.guestOnly
            const forAll = current.meta.forAll

            if (guestOnly) {
              if (authToken) {
                this.$router.replace({ name: 'dashboard' })
              }
            } else if (!forAll) {
              if (!authToken) {
                this.signOut()
                this.$router.push({ name: 'sign-in' })
              }
            }
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations([
      'setErrorData',
      'clearAllUserData'
    ]),
    syncAuthTokenAndCookie () {
      const authToken = getAuthToken()
      const authCookie = getAuthCookie()

      if (authToken && !authCookie) {
        removeAuthData()
      }
    },
    signOut () {
      if (this.userData) {
        storageManager.initUser(this.userData.userNum)
      }

      clearAllApiCache()
      this.clearAllUserData()
      removeAuthData()
    }
  }
}
</script>

<style lang="scss" scoped>
#app {
  font-family: 'Roboto', 'Noto Sans KR','Noto Sans','맑은 고딕','Malgun Gothic','Nanum Gothic','나눔고딕','NanumGothic','돋움','Dotum', sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.layout {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  transition: opacity 0.3s ease;
  opacity: 1;

& /deep/ .layout-transition-up {
    transition: margin-bottom 0.3s ease, transform 0.5s ease, opacity 0.5s ease;
  }

& /deep/ .layout-transition-left {
    transition: transform 0.5s ease, opacity 0.5s ease;
  }

& /deep/ .layout-transition-fade {
    transition: height 0.3s ease, opacity 0.5s ease;
  }

&.layout-enter,
&.layout-leave-to {
& /deep/ .layout-transition-up {
    opacity: 0;
    transform: translateY(-200px);
  }

& /deep/ .layout-transition-left {
    opacity: 0;
    transform: translateX(-200px);
  }

& /deep/ .layout-transition-fade {
    opacity: 0;
  }
}
}
</style>
